import type { SuggestionsProvider } from "@/shared/components/filters/suggestions";
import type { IdSuggestions } from "@/shared/components/filters/useSuggestionSources";
import type { FilterDefinition } from "@/shared/components/ResourceView";
import type { CustomFieldRecord } from "@/shared/domain/customFields";

import type { DashboardCustomFieldEntity } from "./dashboardPropertyFilters";

/** What {@link withDashboardSuggestions} decorates the dashboard's rows with. */
export interface DashboardSuggestionSources {
  /** The org's id suggestions; `undefined` without an org. */
  ids: IdSuggestions | undefined;
  /** Each related entity's Ready custom fields, as the menu built them. */
  customFields: {
    entity: DashboardCustomFieldEntity;
    fields: readonly CustomFieldRecord[];
  }[];
  /** A String custom field's value suggestions. */
  valueSuggestions: (field: CustomFieldRecord) => SuggestionsProvider;
}

/**
 * The dashboard's filter rows with value suggestions attached: the id rows
 * suggest the org's ids, and each String custom field the values its entities
 * hold. Keyed by the wire field (`device.device_id`,
 * `<entity>.custom.<name>`), so a row this doesn't know passes through
 * untouched, as does one that already has suggestions.
 */
export function withDashboardSuggestions(
  definitions: FilterDefinition[],
  { ids, customFields, valueSuggestions }: DashboardSuggestionSources,
): FilterDefinition[] {
  const byField = new Map<string, () => SuggestionsProvider>();
  if (ids !== undefined) {
    byField.set("device.device_id", () => ids.device);
    byField.set("session.id", () => ids.session);
    byField.set("collection.collection_id", () => ids.collection);
  }
  for (const { entity, fields } of customFields) {
    for (const field of fields) {
      byField.set(`${entity}.custom.${field.field_name}`, () =>
        valueSuggestions(field),
      );
    }
  }
  return definitions.map((definition) => {
    const provider = byField.get(definition.field);
    if (
      definition.type !== "string" ||
      definition.suggestions !== undefined ||
      provider === undefined
    ) {
      return definition;
    }
    return { ...definition, suggestions: provider() };
  });
}
